import { useEffect, useState } from "react";
import DropdownInput from "../components/dropdownInput";
import SideMenu from "../components/sideMenu";
import {
  Device,
  HourlyEntry,
  Organization,
  RawData,
  ResolutionFilter,
} from "../type";
import ResolutionRadio from "../components/resolutionFilter";
import BarChartComponent from "../components/barchartComponent";
import { organizationService } from "../services/organizationService";
import { deviceService } from "../services/deviceService";
import {
  downloadCsv,
  getBarchartData,
  getOneDayStatsByRange,
  getRawDataWithPagination,
} from "../services/dataService";
import Loading from "../components/loading";
import BarChartOneDayComponent from "../components/barchartOneDayComponent";
import axios from "axios";
import { FaSortNumericDown, FaSortNumericUpAlt } from "react-icons/fa";
import dayjs from "dayjs";

const PAGE_SIZE = 20;
const PAGE_RANGE = 2;

// Limit สำหรับการ Fetch Dropdown
const ORG_LIMIT = 10;
const DEVICE_LIMIT = 10;

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";
const token = localStorage.getItem("token");

const HistoricalPage: React.FC = () => {
  const [selectFilter, setSelectFilter] = useState<number>();
  const [resolution, setResolution] = useState<ResolutionFilter>(
    ResolutionFilter.HOURLY,
  );
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectOrgIdx, setSelectOrgIdx] = useState<number>();
  const [devices, setDevices] = useState<Device[]>([]);
  const [selectDeviceIdx, setSelectDeviceIdx] = useState<number>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [rawTotal, setRawTotal] = useState<number>();
  const [sort, setSort] = useState<"asc" | "desc">("desc");
  const [dataDateAcc, setDataDateAcc] = useState<
    { name: string; captured_co2: number; generated_o2: number }[]
  >([]);
  const [selectDays, setSelectDays] = useState({
    day_0: true,
    day_1: true,
    day_2: true,
    day_3: true,
    day_4: true,
    day_5: true,
    day_6: true,
  });

  // --- Pagination States สำหรับ Dropdown ---
  const [orgPage, setOrgPage] = useState(1);
  const [totalOrgs, setTotalOrgs] = useState(0);
  const [devicePage, setDevicePage] = useState(1);
  const [totalDevices, setTotalDevices] = useState(0);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [rawData, setRawData] = useState<RawData[]>([]);

  const generateHourlyData = () => {
    return Array.from({ length: 24 }, (_, hour) => {
      const name = hour.toString().padStart(2, "0") + ":00";
      return {
        name,
        value_day_0: 0,
        value_day_1: 0,
        value_day_2: 0,
        value_day_3: 0,
        value_day_4: 0,
        value_day_5: 0,
        value_day_6: 0,
      };
    });
  };

  const [historyDataCo2, setHistoryDataCo2] = useState(generateHourlyData());
  const [historyDataO2, setHistoryDataO2] = useState(generateHourlyData());

  // --- MAIN FETCH FUNCTION (รวมการดึงข้อมูล 3 แบบไว้ที่เดียว ลดปัญหา State ผิดเพี้ยน) ---
  const executeSearch = async (
    targetDeviceId: number,
    targetStart: string,
    targetEnd: string,
    targetPage: number
  ) => {
    setIsLoading(true);
    try {
      // 1. Raw Data
      const raws = await getRawDataWithPagination(
        targetDeviceId,
        targetStart,
        targetEnd,
        targetPage,
        PAGE_SIZE,
        sort
      );
      if (raws && raws.data) {
        setRawData(raws.data);
        setRawTotal(raws.total);
        setTotalPages(Math.ceil(raws.total / PAGE_SIZE));
      } else {
        setRawData([]);
        setRawTotal(0);
        setTotalPages(1);
      }

      // 2. Bar Chart
      const barchartData = await getBarchartData(
        targetDeviceId,
        targetStart,
        targetEnd,
        resolution
      );
      if (barchartData?.data === null) {
        setDataDateAcc([]);
      } else if (Array.isArray(barchartData)) {
        const newBarChart = barchartData.map((b: any) => ({
          name: b.groupedTime,
          captured_co2: b.gCO2,
          generated_o2: b.gO2,
        }));
        setDataDateAcc(newBarChart);
      } else {
        setDataDateAcc([]);
      }

      // 3. One Day Barchart
      let selectedDays = [];
      if (selectDays.day_0) selectedDays.push(0);
      if (selectDays.day_1) selectedDays.push(1);
      if (selectDays.day_2) selectedDays.push(2);
      if (selectDays.day_3) selectedDays.push(3);
      if (selectDays.day_4) selectedDays.push(4);
      if (selectDays.day_5) selectedDays.push(5);
      if (selectDays.day_6) selectedDays.push(6);

      const oneDayData = await getOneDayStatsByRange(
        targetDeviceId,
        targetStart,
        targetEnd,
        selectedDays
      );

      if (oneDayData?.data === null) {
        setHistoryDataCo2(generateHourlyData());
        setHistoryDataO2(generateHourlyData());
      } else if (Array.isArray(oneDayData)) {
        transformOneDayApiData(oneDayData);
      } else {
        setHistoryDataCo2(generateHourlyData());
        setHistoryDataO2(generateHourlyData());
      }
    } catch (error) {
      console.error("Execute Search Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // --- Fetch เฉพาะ Raw Data เมื่อเปลี่ยนหน้า (Pagination) หรือเปลี่ยน Sort ---
  const fetchRawDataOnly = async (page: number, currentSort: string) => {
    if (selectDeviceIdx !== undefined && devices[selectDeviceIdx]) {
      setIsLoading(true);
      try {
        const raws = await getRawDataWithPagination(
          devices[selectDeviceIdx].deviceId,
          startDate,
          endDate,
          page,
          PAGE_SIZE,
          currentSort
        );
        if (raws && raws.data) {
          setRawData(raws.data);
          setRawTotal(raws.total);
          setTotalPages(Math.ceil(raws.total / PAGE_SIZE));
        } else {
          setRawData([]);
          setRawTotal(0);
          setTotalPages(1);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (selectDeviceIdx !== undefined && devices.length > 0 && startDate && endDate) {
      fetchRawDataOnly(currentPage, sort);
    }
  }, [currentPage, sort]);


  const transformOneDayApiData = (apiData: any[]) => {
    const co2Data = generateHourlyData();
    const o2Data = generateHourlyData();

    apiData.forEach((dayItem: any) => {
      const { day, data } = dayItem;
      if (!data || data.length === 0) return;

      const historical = data[0].historical;
      if (!historical) return;

      Object.entries(historical).forEach(([hour, values]) => {
        const hourIndex = parseInt(hour);
        if (!isNaN(hourIndex)) {
          const co2Value = (values as any).gCO2 ?? 0;
          const o2Value = (values as any).gO2 ?? 0;

          (co2Data[hourIndex][
            `value_day_${day}` as keyof HourlyEntry
          ] as number) = co2Value;
          (o2Data[hourIndex][
            `value_day_${day}` as keyof HourlyEntry
          ] as number) = o2Value;
        }
      });
    });

    setHistoryDataCo2(co2Data);
    setHistoryDataO2(o2Data);
  };

  // --- ฟังก์ชันจัดเตรียมวันที่ตอนกดปุ่ม Quick Filter 7 วัน, 1 เดือน ฯลฯ ---
  const handleFilterChange = (option: number) => {
    setSelectFilter(option);

    const today = new Date();
    let start = new Date(today);

    switch (option) {
      case 0:
        start.setDate(today.getDate() - 7);
        break;
      case 1:
        start.setMonth(today.getMonth() - 1);
        break;
      case 2:
        start.setMonth(today.getMonth() - 3);
        break;
      case 3:
        start.setFullYear(today.getFullYear() - 1);
        break;
      default:
        start = today;
    }

    // แปลง Format ให้ตรงกับที่ input datetime-local ต้องการ (YYYY-MM-DDTHH:mm)
    const newStart = dayjs(start).format("YYYY-MM-DDTHH:mm");
    const newEnd = dayjs(today).format("YYYY-MM-DDTHH:mm");

    setStartDate(newStart);
    setEndDate(newEnd);

    // สั่งโหลดข้อมูลใหม่ให้ทันที โดยไม่ต้องรอกด Search
    if (selectDeviceIdx !== undefined && devices[selectDeviceIdx]) {
      setCurrentPage(1);
      executeSearch(devices[selectDeviceIdx].deviceId, newStart, newEnd, 1);
    }
  };

  const toggleFilter = (option: number) => {
    if (selectFilter === option) {
      setSelectFilter(undefined);
    } else {
      handleFilterChange(option);
    }
  };


  // --- ฟังก์ชันเริ่มต้นระบบ ---
  const initialize = async () => {
    try {
      setIsLoading(true);
      const fetchedOrgs = await organizationService.getOrganizations({ page: 1, limit: ORG_LIMIT });
      setOrganizations(fetchedOrgs.data);
      setTotalOrgs(fetchedOrgs.total ?? 0);

      if (fetchedOrgs.data && fetchedOrgs.data.length > 0) {
        setSelectOrgIdx(0);
        const orgName = fetchedOrgs.data[0].orgName;

        const devicesRes = await deviceService.getDevices({ org_name: orgName, page: 1, limit: DEVICE_LIMIT });
        setDevices(devicesRes.data);
        setTotalDevices(devicesRes.total ?? 0);

        if (devicesRes.data && devicesRes.data.length > 0) {
          setSelectDeviceIdx(0);
          const firstDeviceId = devicesRes.data[0].deviceId;

          // กำหนดวันเริ่มต้น (7 วันย้อนหลัง)
          const today = new Date();
          const start = new Date(today);
          start.setDate(today.getDate() - 7);
          const newStart = dayjs(start).format("YYYY-MM-DDTHH:mm");
          const newEnd = dayjs(today).format("YYYY-MM-DDTHH:mm");

          setStartDate(newStart);
          setEndDate(newEnd);
          setSelectFilter(0);

          // รันการค้นหาทันทีด้วย device แรก
          executeSearch(firstDeviceId, newStart, newEnd, 1);
        }
      }
    } catch (error) {
      console.error("Initialization failed:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    initialize();
  }, []);

  // --- Handlers ---
  const onSelectOrg = async (orgIdx: number) => {
    try {
      setSelectOrgIdx(orgIdx);
      setDevicePage(1);

      // ล้างข้อมูลเก่าของ Org เดิมทิ้ง
      setRawData([]);
      setDataDateAcc([]);
      setHistoryDataCo2(generateHourlyData());
      setHistoryDataO2(generateHourlyData());

      setIsLoading(true);
      const orgName = organizations[orgIdx].orgName;
      const devicesRes = await deviceService.getDevices({ org_name: orgName, page: 1, limit: DEVICE_LIMIT });

      setDevices(devicesRes.data);
      setTotalDevices(devicesRes.total ?? 0);

      // ถ้า Org ใหม่มีเครื่อง ให้ตั้งค่าเป็นเครื่องแรกและโหลดข้อมูลทันที
      if (devicesRes.data && devicesRes.data.length > 0) {
        setSelectDeviceIdx(0);
        setCurrentPage(1);
        executeSearch(devicesRes.data[0].deviceId, startDate, endDate, 1);
      } else {
        setSelectDeviceIdx(undefined);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const onSelectDevice = async (deviceIdx: number) => {
    setSelectDeviceIdx(deviceIdx);
    setCurrentPage(1);
    executeSearch(devices[deviceIdx].deviceId, startDate, endDate, 1);
  };

  const searchData = () => {
    if (selectDeviceIdx !== undefined && devices[selectDeviceIdx]) {
      setCurrentPage(1);
      executeSearch(devices[selectDeviceIdx].deviceId, startDate, endDate, 1);
    }
  };

  const handleLoadMoreOrgs = async () => {
    if (organizations.length < totalOrgs) {
      const nextPage = orgPage + 1;
      setOrgPage(nextPage);
      try {
        setIsLoading(true);
        const res = await organizationService.getOrganizations({ page: nextPage, limit: ORG_LIMIT });
        setOrganizations((prev) => [...prev, ...res.data]);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleLoadMoreDevices = async () => {
    if (devices.length < totalDevices && selectOrgIdx !== undefined) {
      const nextPage = devicePage + 1;
      setDevicePage(nextPage);
      try {
        setIsLoading(true);
        const orgName = organizations[selectOrgIdx].orgName;
        const res = await deviceService.getDevices({ org_name: orgName, page: nextPage, limit: DEVICE_LIMIT });
        setDevices((prev) => [...prev, ...res.data]);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // --- UI Helpers ---
  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getPagination = () => {
    const pages: (number | string)[] = [];
    if (totalPages <= 10) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    pages.push(1);
    if (currentPage > PAGE_RANGE + 2) pages.push("...");
    for (
      let i = Math.max(2, currentPage - PAGE_RANGE);
      i <= Math.min(totalPages - 1, currentPage + PAGE_RANGE);
      i++
    ) {
      pages.push(i);
    }
    if (currentPage < totalPages - PAGE_RANGE - 1) pages.push("...");
    pages.push(totalPages);
    return pages;
  };

  return (
    <>
      <SideMenu active={1}>
        <div className="relative w-full overflow-x-hidden">
          {isLoading && <Loading />}

          <div className="px-4 py-4 max-w-6xl mx-auto pt-8 xl:pt-4">
            <div className="flex flex-col md:flex-row justify-between items-start gap-4 rounded-lg w-full">
              <h3 className="text-lg md:text-xl font-bold">
                Historical captured CO2 <br className="lg:hidden" />
                and Generated O2 data
              </h3>
              {selectOrgIdx !== undefined && (
                <div className="w-full md:w-64">
                  <DropdownInput
                    options={organizations.map((org) => org.orgName)}
                    onSelect={(e) => onSelectOrg(e)}
                    value_idx={selectOrgIdx}
                    onReachBottom={handleLoadMoreOrgs}
                  />
                </div>
              )}
            </div>

            <div className="mt-8">
              <p className="font-semibold mb-2">Device</p>
              <div className="flex flex-col md:flex-row gap-4 md:gap-5 rounded-lg w-full">
                {selectDeviceIdx !== undefined && (
                  <div className="w-full md:w-64">
                    <DropdownInput
                      options={devices.map((d) => d.deviceName)}
                      value_idx={selectDeviceIdx}
                      onSelect={(e) => onSelectDevice(e)}
                      onReachBottom={handleLoadMoreDevices}
                    />
                  </div>
                )}

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 w-full">
                  <button
                    className={`rounded border px-3 py-1 text-sm ${
                      selectFilter === 0
                        ? "border-green text-green bg-green-light/50"
                        : "border-gray-nor text-gray-nor"
                    }`}
                    onClick={() => toggleFilter(0)}
                  >
                    7 days ago
                  </button>
                  <button
                    className={`rounded border px-3 py-1 text-sm ${
                      selectFilter === 1
                        ? "border-green text-green bg-green-light/50"
                        : "border-gray-nor text-gray-nor"
                    }`}
                    onClick={() => toggleFilter(1)}
                  >
                    1 month ago
                  </button>
                  <button
                    className={`rounded border px-3 py-1 text-sm ${
                      selectFilter === 2
                        ? "border-green text-green bg-green-light/50"
                        : "border-gray-nor text-gray-nor"
                    }`}
                    onClick={() => toggleFilter(2)}
                  >
                    3 month ago
                  </button>
                  <button
                    className={`rounded border px-3 py-1 text-sm ${
                      selectFilter === 3
                        ? "border-green text-green bg-green-light/50"
                        : "border-gray-nor text-gray-nor"
                    }`}
                    onClick={() => toggleFilter(3)}
                  >
                    12 month ago
                  </button>
                </div>
              </div>

              <div className="mt-5 flex flex-col md:flex-row md:items-end gap-4 w-full flex-wrap">
                <div className="flex flex-col w-full md:w-1/3">
                  <p className="mb-2 text-sm">Start Date</p>
                  <input
                    type="datetime-local"
                    name="start"
                    id="start"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="border border-gray-nor focus:outline-none px-3 py-2 rounded text-sm"
                  />
                </div>
                <div className="flex flex-col w-full md:w-1/3">
                  <p className="mb-2 text-sm">End Date</p>
                  <input
                    type="datetime-local"
                    name="end"
                    id="end"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="border border-gray-nor focus:outline-none px-3 py-2 rounded text-sm"
                  />
                </div>
                <div className="flex flex-col w-full md:w-auto">
                  <p className="mb-2 text-sm">Resolution</p>
                  <ResolutionRadio
                    is_mins={false}
                    resolution={resolution}
                    setResolution={setResolution}
                  />
                </div>
              </div>

              <div className="mt-5 flex flex-col gap-4 items-start">
                <p className="text-sm">Days</p>
                <div className="grid grid-cols-3 md:grid-cols-7 gap-3 text-sm">
                  <div className="flex gap-2 items-center">
                    <input
                      type="checkbox"
                      id="day_0"
                      checked={selectDays.day_0}
                      onChange={() =>
                        setSelectDays((prev) => ({
                          ...prev,
                          day_0: !prev.day_0,
                        }))
                      }
                      className="pointer-cursor accent-[#FF6384]"
                    />
                    <label htmlFor="day_0">Sunday</label>
                  </div>
                  <div className="flex gap-2 items-center">
                    <input
                      type="checkbox"
                      id="day_1"
                      checked={selectDays.day_1}
                      onChange={() =>
                        setSelectDays((prev) => ({
                          ...prev,
                          day_1: !prev.day_1,
                        }))
                      }
                      className="pointer-cursor accent-[#FFCE56]"
                    />
                    <label htmlFor="day_1">Monday</label>
                  </div>
                  <div className="flex gap-2 items-center">
                    <input
                      type="checkbox"
                      id="day_2"
                      checked={selectDays.day_2}
                      onChange={() =>
                        setSelectDays((prev) => ({
                          ...prev,
                          day_2: !prev.day_2,
                        }))
                      }
                      className="pointer-cursor accent-[#FF69B4]"
                    />
                    <label htmlFor="day_2">Tuesday</label>
                  </div>
                  <div className="flex gap-2 items-center">
                    <input
                      type="checkbox"
                      id="day_3"
                      checked={selectDays.day_3}
                      onChange={() =>
                        setSelectDays((prev) => ({
                          ...prev,
                          day_3: !prev.day_3,
                        }))
                      }
                      className="pointer-cursor accent-[#4EA72E]"
                    />
                    <label htmlFor="day_3">Wednesday</label>
                  </div>
                  <div className="flex gap-2 items-center">
                    <input
                      type="checkbox"
                      id="day_4"
                      checked={selectDays.day_4}
                      onChange={() =>
                        setSelectDays((prev) => ({
                          ...prev,
                          day_4: !prev.day_4,
                        }))
                      }
                      className="pointer-cursor accent-[#FF9F40]"
                    />
                    <label htmlFor="day_4">Thursday</label>
                  </div>
                  <div className="flex gap-2 items-center">
                    <input
                      type="checkbox"
                      id="day_5"
                      checked={selectDays.day_5}
                      onChange={() =>
                        setSelectDays((prev) => ({
                          ...prev,
                          day_5: !prev.day_5,
                        }))
                      }
                      className="pointer-cursor accent-[#36A2EB]"
                    />
                    <label htmlFor="day_5">Friday</label>
                  </div>
                  <div className="flex gap-2 items-center">
                    <input
                      type="checkbox"
                      id="day_6"
                      checked={selectDays.day_6}
                      onChange={() =>
                        setSelectDays((prev) => ({
                          ...prev,
                          day_6: !prev.day_6,
                        }))
                      }
                      className="pointer-cursor accent-[#9966FF]"
                    />
                    <label htmlFor="day_6">Saturday</label>
                  </div>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-3 items-center mt-6">
                <button
                  className="px-5 py-2 w-full md:w-36 shadow rounded bg-dark-green text-white text-sm"
                  onClick={searchData}
                >
                  SEARCH
                </button>
                <p className="text-gray-nor text-xs max-w-xl">
                  remark: the bar chart illustrates the accumulate of each
                  resolution by not include missing data
                </p>
              </div>

              {rawData.length > 0 && (
                <div className="mt-8">
                  <div className="w-[370px] md:w-[720px] xl:w-[1000px]">
                    <BarChartComponent
                      data={dataDateAcc}
                      chartName="Captured CO2 and Generated O2 Chart"
                    />
                  </div>

                  <div className="w-[370px] md:w-[720px] xl:w-[1000px] mt-6">
                    <BarChartOneDayComponent
                      data={historyDataCo2}
                      days={selectDays}
                      chartName="Captured CO2 in 24 Hours Chart"
                    />
                  </div>

                  <div className="w-[370px] md:w-[720px] xl:w-[1000px] mt-6">
                    <BarChartOneDayComponent
                      data={historyDataO2}
                      days={selectDays}
                      chartName="Generated O2 in 24 Hours Chart"
                    />
                  </div>
                </div>
              )}

              {rawData.length > 0 && (
                <div className="mt-8">
                  <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between w-full">
                    <div className="max-w-full">
                      <p className="font-semibold text-base md:text-lg text-ellipsis overflow-hidden">
                        Raw log data from device in selected time
                      </p>
                      <p className="text-sm">
                        ({rawTotal?.toLocaleString("th-TH")} rows)
                      </p>
                    </div>
                    <div className="flex gap-4">
                      <button className="flex gap-2 items-center font-medium mt-2 md:mt-0 border border-green text-sm text-green px-4 py-2 rounded-lg shadow-md hover:bg-green-light/50"
                      onClick={() => setSort(sort == "asc" ? "desc" : "asc")}>
                        Sort {sort == "asc" ? <FaSortNumericDown/> : <FaSortNumericUpAlt/>}
                      </button>
                      <button
                        className="mt-2 md:mt-0 bg-dark-green text-sm text-white px-4 py-2 rounded-lg shadow-md hover:bg-green/50"
                        onClick={async () => {
                          if (selectDeviceIdx !== undefined) {
                            setIsLoading(true);
                            try {
                              const deviceId = devices[selectDeviceIdx].deviceId;
                              const { downloadUrl } = await downloadCsv(
                                deviceId,
                                startDate,
                                endDate,
                              );

                              const fileResponse = await axios.get(
                                `${API_BASE_URL}${downloadUrl}`,
                                {
                                  responseType: "blob",
                                  headers: {
                                    Authorization: `Bearer ${token}`,
                                  },
                                },
                              );

                              const blob = new Blob([fileResponse.data], {
                                type: "text/csv",
                              });
                              const url = window.URL.createObjectURL(blob);
                              const link = document.createElement("a");
                              link.href = url;
                              const now = new Date()
                                .toISOString()
                                .replace(/[:.]/g, "-");
                              link.download = `iot_data_${deviceId}_${now}.csv`;
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                              window.URL.revokeObjectURL(url);
                            } catch (err) {
                              console.error("Download failed:", err);
                            }
                            setIsLoading(false);
                          }
                        }}
                      >
                        Download CSV file
                      </button>
                    </div>
                  </div>

                  <div className="text-sm my-5 w-[380px] md:w-[720px] xl:w-full overflow-x-auto no-scrollbar">
                    <div className="w-[900px] xl:w-full border border-gray-200 rounded-lg overflow-hidden">
                      <div className="grid grid-cols-9 bg-navy text-white p-4 text-center">
                        <div className="col-span-1">Timestamp</div>
                        <div className="col-span-1">Inlet CO2 (ppm)</div>
                        <div className="col-span-1">Inlet O2 (%)</div>
                        <div className="col-span-1">Inlet RH (%)</div>
                        <div className="col-span-1">Inlet Temperature (°C)</div>
                        <div className="col-span-1">Outlet CO2 (ppm)</div>
                        <div className="col-span-1">Outlet O2 (%)</div>
                        <div className="col-span-1">Outlet RH (%)</div>
                        <div className="col-span-1">Outlet Temperature (°C)</div>
                      </div>

                      <div className="max-h-[500px] overflow-y-auto bg-white">
                        {rawData.map((data, i) => (
                          <div
                            key={i}
                            className={`grid grid-cols-9 ${
                              i % 2 === 0 ? "bg-white" : "bg-green-light/30"
                            } text-black p-4 text-center border-b border-gray-100 hover:bg-gray-50`}
                          >
                            <div className="col-span-1">
                              {dayjs(data.timestamp).format("DD MMM YYYY, HH:mm")}
                            </div>
                            <div className="col-span-1">
                              {data.co2Inlet.toFixed(2)}
                            </div>
                            <div className="col-span-1">
                              {data.o2Inlet.toFixed(2)}
                            </div>
                            <div className="col-span-1">
                              {data.co2RHInlet.toFixed(2)}
                            </div>
                            <div className="col-span-1">
                              {data.co2TempInlet.toFixed(2)}
                            </div>
                            <div className="col-span-1">
                              {data.co2Outlet.toFixed(2)}
                            </div>
                            <div className="col-span-1">
                              {data.o2Outlet.toFixed(2)}
                            </div>
                            <div className="col-span-1">
                              {data.co2RHOutlet.toFixed(2)}
                            </div>
                            <div className="col-span-1">
                              {data.co2TempOutlet.toFixed(2)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-row items-center justify-center gap-3 w-full mt-4">
                    <button
                      onClick={() => goToPage(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <img
                        src="./right-arrow.svg"
                        alt="Previous"
                        className="w-5 rotate-180 opacity-80"
                      />
                    </button>

                    <div className="flex space-x-2 overflow-x-auto max-w-xs justify-center">
                      {getPagination().map((page, i) => (
                        <button
                          key={i}
                          className={`px-3 py-1 rounded text-sm ${
                            currentPage === page
                              ? "bg-green text-white"
                              : "bg-gray-nor text-black"
                          } ${page === "..." ? "cursor-default" : ""}`}
                          onClick={() =>
                            typeof page === "number" && goToPage(page)
                          }
                          disabled={page === "..."}
                        >
                          {page}
                        </button>
                      ))}
                    </div>

                    <button
                      onClick={() => goToPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      <img
                        src="./right-arrow.svg"
                        alt="Next"
                        className="w-5 opacity-80"
                      />
                    </button>
                  </div>
                </div>
              )}

              {rawData.length === 0 && (
                <div className="text-center mt-24 text-gray-nor">No Data</div>
              )}
            </div>
          </div>
        </div>
      </SideMenu>
    </>
  );
};

export default HistoricalPage;